/**
 * Procedural kitchen-sink basin + faucet emitters — the SINGLE source for both
 * the manual cabinet-run engine and the auto generator (`materializeCandidate`
 * calls `buildKitchenSinkBasin` / `buildKitchenFaucet` here; it used to carry
 * near-identical private copies, now removed). Object shapes (`kitchen-sink` /
 * `kitchen-faucet`), dimensions, cutout profiles, and parent links match, so the
 * existing 2D/3D renderers (the countertop CSG cutout, the sink/faucet meshes)
 * display them with no per-source rendering code. `id` and `addedBy` are caller-
 * supplied so each source keeps its own id scheme + provenance tag. Pure.
 */

const { getWallAxes } = require('./wallMath');
const {
  KITCHEN_CABINET_DIMENSIONS,
  KITCHEN_COUNTERTOP_THICKNESS,
  KITCHEN_SINK_BASIN,
  KITCHEN_FAUCET_DIMS,
  INCHES_TO_METERS,
} = require('./constants');
const {
  buildKitchenSinkCutout,
  buildKitchenSinkCabinetCutout,
  buildKitchenSinkDimensions,
} = require('./sinkParts');
const { kitchenSinkSeatY, targetFrontAlong } = require('./sinkSeat');

// Back edge of the faucet bbox sits this far forward of the wall (= flush with
// the back of the sink cabinet).
const FAUCET_FROM_WALL = 1 * INCHES_TO_METERS;

/**
 * Distance from the wall plane to the countertop's FRONT face, along the
 * wall's inward normal. The slab's back sits on the wall, so this is what
 * `seatSinkOnCounter` calls `counterFront` — projected here rather than taken
 * from `dimensions.z` directly so an overhang or a peninsula cover panel that
 * moved the slab's centre is respected.
 */
function counterFrontFromWall(countertop, axes) {
  const n = axes.wallNormal;
  const c = countertop.center;
  const along = (c.x - axes.start.x) * n.x + (c.z - axes.start.z) * n.z;
  return along + (countertop.dimensions?.z ?? 0) / 2;
}

/**
 * Build a kitchen-sink basin sunk into the countertop above a sink cabinet.
 * `range` is the sink cabinet's along-wall {start,end} param. Returns null when
 * the wall axes are unavailable.
 */
function buildKitchenSinkBasin({
  id,
  wall,
  range,
  floorY,
  countertopId,
  countertop,
  sinkCabinetId,
  fixtureGroupId,
  addedBy,
  subtype = 'undermount',
}) {
  const axes = getWallAxes(wall);
  if (!axes) return null;
  const ry = axes.wallRotationY;
  const wallParam = (range.start + range.end) / 2;
  const countertopTopY =
    floorY + KITCHEN_CABINET_DIMENSIONS.height + KITCHEN_COUNTERTOP_THICKNESS;
  // How far out from the wall the basin's CENTRE sits.
  //
  // Given the slab it drops into, that is the scan path's rule and not a
  // second one: `targetFrontAlong` puts an undermount's front
  // `UNDERMOUNT_FRONT_SETBACK` behind the counter's face and an apron
  // `APRON_PROUD` in FRONT of it — an apron is meant to stick out, so "the
  // basin fits inside its cabinet" was never the invariant. Seating from the
  // real slab is also the only way the answer can follow a run's depth: this
  // used to be the constant `KITCHEN_CABINET_DIMENSIONS.depth / 2`, which put
  // the basin 12" from the wall whatever the cabinet under it was, so a
  // 12"-deep run hung a 17" bowl 8.5" past its own counter, over the floor.
  //
  // With NO slab it stays the constant. The generator builds the basin before
  // its countertop exists (`materializeCandidate` — the engine parents it
  // onto the slab afterwards), so that path is unchanged.
  //
  // And it is clamped so the bowl never reaches BEHIND the wall. A counter
  // too shallow to contain a 17" bowl has to put it somewhere, and the
  // answer is OUTWARD (user ruling 2026-09-21): the bowl slides into the
  // room, proud of the counter like an apron, rather than into the wall it
  // is plumbed through. `KITCHEN_SINK_BASIN.depth / 2` is the centre whose
  // BACK sits exactly on the wall plane — the cabinet's own back face — so
  // this is a physical limit rather than a chosen margin, and at any depth
  // that can hold the bowl the clamp never binds and the setback above is
  // what applies.
  const centreFromWall = countertop
    ? Math.max(
        targetFrontAlong(counterFrontFromWall(countertop, axes), subtype) -
          KITCHEN_SINK_BASIN.depth / 2,
        KITCHEN_SINK_BASIN.depth / 2
      )
    : KITCHEN_CABINET_DIMENSIONS.depth / 2;
  const center = {
    x:
      axes.start.x +
      wallParam * axes.wallDir.x +
      centreFromWall * axes.wallNormal.x,
    y: kitchenSinkSeatY({
      subtype,
      countertopTopY,
      basinHeight: KITCHEN_SINK_BASIN.height,
    }),
    z:
      axes.start.z +
      wallParam * axes.wallDir.z +
      centreFromWall * axes.wallNormal.z,
  };

  return {
    id,
    type: 'kitchen-sink',
    // Drives the vertical seat above (`kitchenSinkSeatY`) as well as the
    // label and the cutout profile. Defaults to the undermount this
    // generator has always emitted.
    subtype,
    label: 'Kitchen Sink',
    category: { 'kitchen-sink': true },
    center,
    // Dual-shape dimensions (see sinkParts.js for why both are required).
    dimensions: buildKitchenSinkDimensions(KITCHEN_SINK_BASIN),
    rotation: { x: 0, y: ry, z: 0 },
    parents: [
      { id: countertopId, type: 'countertop' },
      ...(sinkCabinetId ? [{ id: sinkCabinetId, type: 'lower-cabinet' }] : []),
    ],
    parentWallIds: [wall.id],
    ...(fixtureGroupId ? { fixtureGroupId } : {}),
    // Cutout profiles — Countertop.jsx applies a 1" radial inset; the cabinet
    // CSG does not (see sinkParts.js).
    'sink-cutout': buildKitchenSinkCutout(KITCHEN_SINK_BASIN),
    'sink-cabinet-cutout': buildKitchenSinkCabinetCutout(KITCHEN_SINK_BASIN),
    addedBy,
    lastModified: new Date().toISOString(),
  };
}

/**
 * Build a kitchen faucet just inside the back rim of the basin, riding on top
 * of the countertop. Returns null when the wall axes are unavailable.
 */
function buildKitchenFaucet({ id, sink, wall, floorY, countertopId, addedBy }) {
  const axes = getWallAxes(wall);
  if (!axes) return null;
  const ry = axes.wallRotationY;
  const countertopTopY =
    floorY + KITCHEN_CABINET_DIMENSIONS.height + KITCHEN_COUNTERTOP_THICKNESS;

  // The faucet stands a fixed FAUCET_FROM_WALL off the wall — its back flush
  // with the back of the sink cabinet — so it is placed from the WALL, not
  // from the sink.
  //
  // It used to be derived as `sink.center - (assumedSinkFromWall -
  // faucetFromWall)`, where the assumption was the constant
  // `KITCHEN_CABINET_DIMENSIONS.depth / 2`. That is the same answer only
  // while the basin really is at half the standard depth; now the basin is
  // seated from its slab (see `buildKitchenSinkBasin`), a derived faucet
  // would inherit the basin's shift and travel with it, away from the wall
  // it hangs on.
  const faucetCenterFromWall = FAUCET_FROM_WALL + KITCHEN_FAUCET_DIMS.depth / 2;
  const wallParam =
    (sink.center.x - axes.start.x) * axes.wallDir.x +
    (sink.center.z - axes.start.z) * axes.wallDir.z;

  const center = {
    x:
      axes.start.x +
      wallParam * axes.wallDir.x +
      faucetCenterFromWall * axes.wallNormal.x,
    y: countertopTopY + KITCHEN_FAUCET_DIMS.height / 2,
    z:
      axes.start.z +
      wallParam * axes.wallDir.z +
      faucetCenterFromWall * axes.wallNormal.z,
  };

  return {
    id,
    type: 'kitchen-faucet',
    label: 'Kitchen Faucet',
    category: { 'kitchen-faucet': true },
    center,
    dimensions: {
      x: KITCHEN_FAUCET_DIMS.width,
      y: KITCHEN_FAUCET_DIMS.height,
      z: KITCHEN_FAUCET_DIMS.depth,
    },
    rotation: { x: 0, y: ry, z: 0 },
    parents: [
      { id: countertopId, type: 'countertop' },
      { id: sink.id, type: 'kitchen-sink' },
    ],
    parentWallIds: [wall.id],
    fixtureGroupId: sink.fixtureGroupId,
    addedBy,
    lastModified: new Date().toISOString(),
  };
}

module.exports = {
  buildKitchenSinkBasin,
  buildKitchenFaucet,
  FAUCET_FROM_WALL,
};
